import type { CSSProperties } from "react";
import { testIds } from "@/testIds";
import {
  displayParts, isBestText, parseNumber, stripUnit,
  type ColumnPlan, type ParsedNumber,
} from "./tableFormat";

/**
 * A number in a decimal-aligned column, split at its point so a column of them
 * lines up the way siunitx's S column does in the PDF: the integer part
 * right-aligned in a box as wide as the column's widest, the fraction (and any
 * `%`) left-aligned in another. Tabular figures make every digit one `ch` wide,
 * which is what lets `ch` stand in for the measurement.
 */
export function DecimalNumber({ n, plan, bold }: { n: ParsedNumber; plan: ColumnPlan; bold?: boolean }) {
  const { int, frac } = displayParts(n);
  return (
    <span
      data-testid={testIds.tableTool.decimalNumber}
      data-int={int}
      data-frac={frac}
      data-best={bold || undefined}
      style={{ display: "inline-flex", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontWeight: bold ? 700 : undefined }}
    >
      <span style={{ display: "inline-block", minWidth: `${plan.intWidth}ch`, textAlign: "right" }}>{int}</span>
      <span style={{ display: "inline-block", minWidth: `${plan.fracWidth}ch`, textAlign: "left" }}>{frac}</span>
    </span>
  );
}

export interface PlannedCell {
  /** The cell's text with the column's unit taken off. */
  text: string;
  /** Set in a decimal column when the cell is a number. */
  number: ParsedNumber | null;
  /** The column's best value: printed bold. */
  best: boolean;
}

/** Read a body cell the way its column means it. */
export function planCell(plan: ColumnPlan | undefined, raw: string): PlannedCell {
  const text = stripUnit(raw ?? "", plan?.unit);
  const number = plan?.decimal ? parseNumber(text) : null;
  return { text, number, best: isBestText(plan, text) };
}

/** A decimal column centres its block of numbers, as siunitx centres an S column. */
export function plannedAlign(plan: ColumnPlan | undefined, fallback: CSSProperties["textAlign"]): CSSProperties["textAlign"] {
  return plan?.decimal ? "center" : fallback;
}
